/**
 * ApiKeysSection: the team's gateway API keys (`llmgw_...`). Any member
 * creates or revokes them; the platform's internal service key is filtered
 * out server-side, so everything shown is user-managed.
 *
 * The raw secret is returned once, on create, so the create flow pops a
 * one-time reveal dialog with a copy button; after it closes the secret is
 * gone for good. Under staff impersonation the list is read-only.
 */

import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { RowAction } from '@/components/settings/RowAction'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import {
  type ApiKey,
  type ApiKeyCreateResponse,
  useApiKeys,
  useRevokeApiKey,
} from '@/hooks/useApiKeys'

import { ApiKeyRevealDialog } from './ApiKeyRevealDialog'
import { scopeLabel } from './apiKeyScopes'
import { CreateApiKeyDialog } from './CreateApiKeyDialog'

// Built once: the formatter is the expensive half of formatting a date.
const DATE_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return DATE_FORMAT.format(d)
}

/** The row's second line: id, creation, last use. */
function keyMeta(key: ApiKey): string {
  const lastUsed = key.last_used_at ? `Last used ${formatDate(key.last_used_at)}` : 'Never used'
  return `${key.key_id} · Created ${formatDate(key.created_at)} · ${lastUsed}`
}

export function ApiKeysSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { impersonatingTeamId } = useTeam()
  const readOnly = impersonatingTeamId !== null
  const { data: keys, isLoading, isError } = useApiKeys()
  const revokeMutation = useRevokeApiKey()

  const [createOpen, setCreateOpen] = useState(false)
  // Holds the just-created key (with secret) for the one-time reveal dialog.
  const [revealed, setRevealed] = useState<ApiKeyCreateResponse | null>(null)
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  const activeKeys = (keys ?? []).filter((k) => !k.revoked_at)

  function handleRevoke(): void {
    if (!keyToRevoke) return
    setRevokeError(null)
    revokeMutation.mutate(
      { keyId: keyToRevoke.key_id },
      {
        onSuccess: () => {
          setKeyToRevoke(null)
        },
        onError: () => {
          setRevokeError('Failed to revoke key. Please try again.')
        },
      },
    )
  }

  return (
    <div>
      <SettingsPageHeader
        section={section}
        action={
          readOnly ? undefined : (
            <Button
              size="sm"
              onClick={() => {
                setCreateOpen(true)
              }}
            >
              <Plus />
              Create key
            </Button>
          )
        }
      />

      <SettingsSection
        title="Active keys"
        description="Treat these like passwords. The secret is shown once, when the key is created. Revoking stops every request using the key immediately."
      >
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive" role="alert">
            Failed to load API keys.
          </p>
        ) : activeKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No API keys yet. Create one to start routing requests through the gateway.
          </p>
        ) : (
          <SettingsList>
            {activeKeys.map((key) => (
              <ApiKeyRow
                key={key.key_id}
                apiKey={key}
                onRevoke={
                  readOnly
                    ? null
                    : () => {
                        setRevokeError(null)
                        setKeyToRevoke(key)
                      }
                }
              />
            ))}
          </SettingsList>
        )}
      </SettingsSection>

      <CreateApiKeyDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={setRevealed} />

      <ApiKeyRevealDialog
        open={revealed !== null}
        onOpenChange={(open) => {
          if (!open) setRevealed(null)
        }}
        apiKey={revealed}
      />

      <ConfirmDialog
        open={keyToRevoke !== null}
        onOpenChange={(open) => {
          if (!open) {
            setKeyToRevoke(null)
            setRevokeError(null)
          }
        }}
        title="Revoke API key"
        description={
          <>
            Revoke <strong>{keyToRevoke?.name}</strong>? Any request using this key stops working
            immediately. This cannot be undone.
          </>
        }
        confirmLabel="Revoke"
        pendingLabel="Revoking..."
        destructive
        isPending={revokeMutation.isPending}
        error={revokeError}
        onConfirm={handleRevoke}
      />
    </div>
  )
}

function ApiKeyRow({
  apiKey,
  onRevoke,
}: {
  apiKey: ApiKey
  onRevoke: (() => void) | null
}): React.JSX.Element {
  return (
    <SettingsListRow>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm text-foreground">{apiKey.name}</span>
          {apiKey.scopes.map((scope) => (
            <Badge key={scope} variant="outline">
              {scopeLabel(scope)}
            </Badge>
          ))}
        </span>
        <span className="truncate font-mono text-xs text-muted-foreground">{keyMeta(apiKey)}</span>
      </span>
      {onRevoke !== null && (
        <RowAction label={`Revoke ${apiKey.name}`} icon={Trash2} destructive onClick={onRevoke} />
      )}
    </SettingsListRow>
  )
}
